// `GET /healthz` and `GET /readyz` — the two operational endpoints (spec/08-api-design.md §4.20).
//
// They are not on `/v1`: no client is generated against them and no domain data passes through
// them. They are here rather than hand-written in the server for the same reason every other
// endpoint is — one schema, one document, one shape — and because the thing that reads them is a
// load balancer, which has even less patience for a response that changed shape than a client does.
//
// Liveness and readiness are deliberately different questions:
//
//   /healthz  Is this process alive? Never touches a dependency. A failing database must not make
//             the orchestrator restart a pod that is working perfectly well.
//   /readyz   Should this process be sent traffic? Checks the dependencies it cannot serve without.
//             Failing here takes the pod out of the load balancer and leaves it running.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { DurationSecondsSchema } from "../primitives/scalars.js";
import { jsonResponse } from "../openapi/responses.js";
/** The four ways one image starts (spec/03-architecture.md §2). */
export const SERVER_ENTRYPOINTS = ['api', 'realtime', 'worker', 'all'];
export const ServerEntrypointSchema = z.enum(SERVER_ENTRYPOINTS).openapi('ServerEntrypoint', {
    description: 'Which entrypoint of the one image this process is running.',
    example: 'api',
});
export const LivenessResponseSchema = z
    .object({
    status: z.literal('ok'),
    entrypoint: ServerEntrypointSchema,
    uptimeSeconds: DurationSecondsSchema,
})
    .openapi('LivenessResponse', { description: 'The process is alive. No dependency was consulted.' });
/** One dependency's verdict. `skipped` is for a dependency this entrypoint does not need. */
export const ReadinessCheckSchema = z
    .object({
    name: z.string().min(1).openapi({ example: 'database' }),
    status: z.enum(['ok', 'failed', 'skipped']),
    /** Why it failed. Never carries a connection string, a credential or a host name. */
    detail: z.string().max(200).optional(),
    tookMs: z.int().nonnegative(),
})
    .openapi('ReadinessCheck');
export const ReadinessResponseSchema = z
    .object({
    status: z.enum(['ok', 'degraded']),
    entrypoint: ServerEntrypointSchema,
    checks: z.array(ReadinessCheckSchema),
})
    .openapi('ReadinessResponse', {
    description: 'Whether this process should be sent traffic, and what each dependency said.',
});
export const healthzRoute = createRoute({
    method: 'get',
    path: '/healthz',
    operationId: 'getLiveness',
    tags: ['meta'],
    summary: 'Liveness',
    description: 'Is this process alive? Answered without touching any dependency.',
    responses: { 200: jsonResponse(LivenessResponseSchema, 'The process is alive.') },
});
export const readyzRoute = createRoute({
    method: 'get',
    path: '/readyz',
    operationId: 'getReadiness',
    tags: ['meta'],
    summary: 'Readiness',
    description: 'Should this process be sent traffic? `503` with the same body when a dependency it cannot serve without is down.',
    responses: {
        200: jsonResponse(ReadinessResponseSchema, 'Ready for traffic.'),
        503: jsonResponse(ReadinessResponseSchema, 'Not ready; at least one dependency failed.'),
    },
});
//# sourceMappingURL=health.js.map